import { isTauri } from "./isTauri";
import type { AutostartState } from "./types";

/**
 * "Start Kobo at login" — the desktop shell's login item.
 *
 * The Rust side is the authority: it registers the login item, re-reads what the
 * OS actually ended up with (a user can veto a login item in System Settings)
 * and pushes that back. Everywhere else — browser, PWA — the concept does not
 * exist, so every call resolves to `unsupported` and callers hide the control
 * rather than offering a switch that silently does nothing.
 */

const CHANGED_EVENT = "busee://autostart-changed";

export const AUTOSTART_UNSUPPORTED: AutostartState = { supported: false, enabled: false };

export async function autostartState(): Promise<AutostartState> {
  if (!isTauri()) {
    return AUTOSTART_UNSUPPORTED;
  }

  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke<AutostartState>("autostart_state");
  } catch (error) {
    console.warn("[Kobo] could not read the login item", error);
    return AUTOSTART_UNSUPPORTED;
  }
}

/**
 * Register or unregister the login item.
 *
 * Resolves to the state that actually took effect, which is not always the state
 * that was asked for — callers should render the result rather than assume.
 */
export async function setAutostart(enabled: boolean): Promise<AutostartState> {
  if (!isTauri()) {
    return AUTOSTART_UNSUPPORTED;
  }

  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke<AutostartState>("autostart_set_enabled", { enabled });
  } catch (error) {
    console.warn("[Kobo] could not change the login item", error);
    return await autostartState();
  }
}

/**
 * Follow changes made outside the app window — the tray menu carries the same
 * toggle, and the two must never disagree.
 *
 * Returns an unsubscribe function; on the web it is a no-op.
 */
export async function onAutostartChanged(
  handler: (state: AutostartState) => void,
): Promise<() => void> {
  if (!isTauri()) {
    return () => {};
  }

  try {
    const { listen } = await import("@tauri-apps/api/event");
    return await listen<AutostartState>(CHANGED_EVENT, (event) => handler(event.payload));
  } catch (error) {
    console.warn("[Kobo] could not subscribe to login-item changes", error);
    return () => {};
  }
}
